<script setup lang="ts">
/**
 * Aura Incorporation — NOTICE & DECLARATION FOR NOMINEE DIRECTOR (Companies Act s.386AL). 이사 1인당 1부(nd_{id}/).
 *   템플릿(Notice for Nominee Director): [[if is_nominee_director]]☒ Yes / ☐ No*[[else]]☐ Yes / ☒ No*[[endif]],
 *   NAME OF NOMINEE DIRECTOR = Yes 면 [[nominee_director_name]], No 면 “N.A.”(원문 안내),
 *   서명란 아래 이름 = [[nominee_director_name]](통지를 받은 이사 본인 — 서명자).
 * ⚠ 조건 플래그는 fill 에 'yes'/'no' 로 기록(화면 로컬 상태만이면 문서에서 거짓으로 평가됨).
 */
import { computed, inject, onMounted } from 'vue';
import CddVarField from './cdd/CddVarField.vue';
import AuraNominatorParticulars from './AuraNominatorParticulars.vue';
import { useCddFill, DOC_SCOPE } from './cdd/useCddFill';
import './allotDoc.css';

const fill = useCddFill();
const scope = inject(DOC_SCOPE, '');
const K = (k: string) => scope + k;

const isNominee = computed(() => String(fill.values[K('is_nominee_director')] ?? '').toLowerCase() === 'yes');
function setNominee(v: boolean) {
  fill.setValue(K('is_nominee_director'), v ? 'yes' : 'no');
}
onMounted(() => {
  if (!String(fill.values[K('is_nominee_director')] ?? '').trim()) fill.setValue(K('is_nominee_director'), 'no');
});
</script>

<template>
  <div class="allot-doc">
    <div class="doc-lbl-title" style="text-align:center">NOTICE &amp; DECLARATION FOR NOMINEE DIRECTOR</div>
    <p class="doc-p"><b>Company Name:</b> <CddVarField field-key="company_name" label="Company Name" /></p>
    <p class="doc-p"><b>Registration No. (UEN):</b> <CddVarField field-key="company_registration_no" label="Registration No. (UEN)" /></p>

    <p class="doc-p">
      This notice is sent under Section 386AL of the Companies Act 1967 of Singapore. Please complete and declare the
      following information; failure to do so as required by this notice may be an offence.
    </p>

    <p class="doc-p"><b>Are you acting as a nominee director<sup>1</sup> of the Company?</b></p>
    <p class="doc-p">
      <span class="cdd-cbx" @click="setNominee(true)"><span class="box">{{ isNominee ? '✓' : '' }}</span> Yes</span>
      /
      <span class="cdd-cbx" @click="setNominee(false)"><span class="box">{{ !isNominee ? '✓' : '' }}</span> No*</span>
    </p>
    <p class="doc-p">
      If your reply is YES, please provide your full name and the particulars of the nominator(s). If your reply is NO,
      kindly indicate “N.A.” in the field for the name of the nominee director.
    </p>
    <p class="doc-p">
      • NAME OF NOMINEE DIRECTOR:
      <template v-if="isNominee"><CddVarField field-key="nominee_director_name" label="Director Name" /></template>
      <b v-else>N.A.</b>
    </p>

    <AuraNominatorParticulars :active="isNominee" />

    <p class="doc-p" style="margin-top:12px">
      I hereby declare that the information provided in this notice is true and accurate to the best of my knowledge. I
      undertake to notify the Company within 30 days if I (become/cease to be) a nominee director of the Company, or if
      there are any changes to the particulars provided herein.
    </p>
    <p class="doc-p">Dated: <CddVarField field-key="dated_on" label="Dated" /></p>
    <p class="doc-p">Yours sincerely,</p>
    <div class="sig-line">_____________________________</div>
    <p class="doc-p" style="margin:2px 0 0">
      <CddVarField field-key="nominee_director_name" label="Director Name (signs this notice)" /><br />Director
    </p>

    <div class="doc-attn nd-foot">
      <p><sup>1</sup><b>Definition of Nominee Director</b></p>
      <p>A nominee director is one who act in accordance with the directions, instructions or wishes of any other person, whether due to a legal obligation (e.g. contract or trust) or informal arrangement.</p>
      <p>Examples:</p>
      <p>• A director is considered a nominee of a shareholder if they are appointed by that shareholder and act in accordance with the shareholder’s directions, instructions, or wishes.</p>
      <p>• A local director appointed to meet the requirement under Section 145(1) of the Companies Act 1967, which states that every company registered in Singapore must have at least one director who is ordinarily a resident in Singapore.</p>
    </div>
  </div>
</template>

<style scoped>
.sig-line { margin-top: 26px; }
.nd-foot { margin-top: 18px; font-size: 11px; }
.nd-foot p { margin: 0 0 3px; }
</style>
